import React, { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import { Fn_FillListData, Fn_AddEditData, Fn_GetReport } from "../../store/Functions";
import { API_WEB_URLS } from "../../constants/constAPI";
import PageTitle from "../layouts/PageTitle";
import { toast } from "react-toastify";

// ─── Imports from modular components ──────────────────────────────────────────
import {
  MONTH_NAMES,
  COLS,
  COL_GROUPS,
  visibleKeys,
  n,
  formatDateForSave,
  emptyRow,
  getEffectiveAllowances,
  computeSingleAllowance,
  computeAllowancesTotal,
  computePFAllowancesAmount,
  computeESIAllowancesAmount,
  getEffectiveDeductions,
  computeSingleDeduction,
  computeDeductionsTotal,
  getEffectiveSalary,
  computeWorkingDays,
  computeBasic,
  computeWages,
  computeOvertime,
  computePF,
  computeESI,
  computeBonus,
  computeGratuity,
  reconstructAllowancesFromRecord,
  reconstructDeductionsFromRecord,
  recomputeRow
} from "./MultiPayrollHelpers";

import {
  KBD,
  TH_NUM,
  thCol,
  rnTd,
  tdStyle,
  getSelRange,
  colLetter,
  GridRow
} from "./MultiPayrollRow";

// ═══════════════════════════════════════════════════════════════════════════════
// ─── Main Component ───────────────────────────────────────────────────────────
// ═══════════════════════════════════════════════════════════════════════════════
const MultiPayrollEntry = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();

  // ── Session (Firm / Month / Year from Home/Dashboard localStorage) ──────────
  const readSession = () => ({
    firm:  (() => { try { return JSON.parse(localStorage.getItem("selectedFirm") || "null"); } catch { return null; } })(),
    month: localStorage.getItem("selectedPayrollMonth") || "",
    year:  parseInt(localStorage.getItem("selectedPayrollYear") || "0"),
  });
  const [sessionFirm,   setSessionFirm]  = useState(() => readSession().firm);
  const [selectedMonth, setSelectedMonth] = useState(() => readSession().month);
  const [selectedYear,  setSelectedYear]  = useState(() => readSession().year);

  useEffect(() => {
    const onStorage = () => {
      const s = readSession();
      setSessionFirm(s.firm);
      setSelectedMonth(s.month);
      setSelectedYear(s.year);
    };
    window.addEventListener("storage", onStorage);
    return () => window.removeEventListener("storage", onStorage);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const isSessionReady = !!sessionFirm?.Id && !!selectedMonth && !!selectedYear;

  // ── Grid state ────────────────────────────────────────────────────────────
  const [rows,          setRows]          = useState(() => Array.from({ length: 10 }, emptyRow));
  const [rowAllowances, setRowAllowances] = useState(() => Array.from({ length: 10 }, () => []));
  const [rowDeductions, setRowDeductions] = useState(() => Array.from({ length: 10 }, () => []));
  const [focus,         setFocus]         = useState({ r: 0, c: 0 });
  const [anchor,        setAnchor]        = useState({ r: 0, c: 0 });
  const [editMode,      setEditMode]      = useState(false);
  const [visibleKeysState, setVisibleKeysState] = useState(() => {
    // Columns NOT shown by default (to match Payroll.jsx display)
    const hideByDefault = new Set([
      'PFApplicable', 'ESIApplicable',  // Flags not shown
      'AdminCharges', 'AC21', 'AC22',   // Internal PF calculations
      'PL_Bal', 'CL_Bal', 'ML_Bal', 'PL_Enjoy', 'CL_Enjoy', 'ML_Enjoy', // Leave balances
      'LeaveWithWages'  // Not shown individually
    ]);
    return new Set(COLS.map(c => c.key).filter(k => !hideByDefault.has(k)));
  });
  const [colPickerOpen, setColPickerOpen] = useState(false);
  const [isLoading,     setIsLoading]     = useState(false);
  const [hoverRowIdx,   setHoverRowIdx]   = useState(null);  // For allowance breakdown tooltip
  const [editValue, _setEditValue] = useState("");
  const editValueRef = useRef("");
  const setLocalEditValue = (val) => {
    _setEditValue(val);
    editValueRef.current = val;
  };

  // ── Pagination state ──────────────────────────────────────────────────────
  const [pageSize, setPageSize] = useState(20);
  const [currentPage, setCurrentPage] = useState(1);

  // ── Master data ───────────────────────────────────────────────────────────
  const [employees,    setEmployees]    = useState([]);
  const [pfRate,       setPfRate]       = useState(null);
  const [esiRate,      setEsiRate]      = useState(null);
  const [bonusRate,    setBonusRate]    = useState(null);
  const [gratuityRate, setGratuityRate] = useState(null);
  const [payrollParams, setPayrollParams] = useState(null);
  const [basicTypeMaster, setBasicTypeMaster] = useState([]);
  const [allowanceMasterList, setAllowanceMasterList] = useState([]);
  const [deductionMasterList, setDeductionMasterList] = useState([]);
  const [leaveMasterList, setLeaveMasterList] = useState([]);
  const [savedRecords,  setSavedRecords] = useState([]); // Loaded payroll records for current month

  // ── Refs (for stale-closure-safe callbacks) ───────────────────────────────
  const rowsRef          = useRef(rows);
  const rowAllowancesRef = useRef(rowAllowances);
  const rowDeductionsRef = useRef(rowDeductions);
  const focusRef         = useRef(focus);
  const anchorRef        = useRef(anchor);
  const editModeRef      = useRef(false);
  const editModeTypeRef  = useRef("typing");
  const editOrigRef      = useRef("");
  const inputRef         = useRef(null);
  const gridRef          = useRef(null);
  const focusedCellRef   = useRef(null);
  const openDropdownRef  = useRef(false);
  const colPickerRef     = useRef(null);
  const visColsRef       = useRef(COLS);
  const pfRateRef        = useRef(pfRate);
  const esiRateRef       = useRef(esiRate);
  const bonusRateRef     = useRef(bonusRate);
  const gratuityRateRef  = useRef(gratuityRate);
  const payrollParamsRef = useRef(payrollParams);
  const employeesRef     = useRef(employees);
  const selectedMonthRef = useRef(selectedMonth);
  const selectedYearRef  = useRef(selectedYear);
  const basicTypeRef     = useRef(basicTypeMaster);
  const allowanceMasterRef = useRef(allowanceMasterList);
  const deductionMasterRef = useRef(deductionMasterList);
  const leaveMasterRef = useRef(leaveMasterList);

  useEffect(() => { rowsRef.current          = rows;           }, [rows]);
  useEffect(() => { rowAllowancesRef.current = rowAllowances;  }, [rowAllowances]);
  useEffect(() => { rowDeductionsRef.current = rowDeductions;  }, [rowDeductions]);
  useEffect(() => { focusRef.current         = focus;          }, [focus]);
  useEffect(() => { anchorRef.current        = anchor;         }, [anchor]);
  useEffect(() => { editModeRef.current      = editMode;       }, [editMode]);
  useEffect(() => { pfRateRef.current        = pfRate;         }, [pfRate]);
  useEffect(() => { esiRateRef.current       = esiRate;        }, [esiRate]);
  useEffect(() => { bonusRateRef.current     = bonusRate;      }, [bonusRate]);
  useEffect(() => { gratuityRateRef.current  = gratuityRate;   }, [gratuityRate]);
  useEffect(() => { payrollParamsRef.current = payrollParams;  }, [payrollParams]);
  useEffect(() => { employeesRef.current     = employees;      }, [employees]);
  useEffect(() => { selectedMonthRef.current = selectedMonth;  }, [selectedMonth]);
  useEffect(() => { selectedYearRef.current  = selectedYear;   }, [selectedYear]);
  useEffect(() => { basicTypeRef.current     = basicTypeMaster;}, [basicTypeMaster]);
  useEffect(() => { allowanceMasterRef.current = allowanceMasterList; }, [allowanceMasterList]);
  useEffect(() => { deductionMasterRef.current = deductionMasterList; }, [deductionMasterList]);
  useEffect(() => { leaveMasterRef.current = leaveMasterList; }, [leaveMasterList]);

  // ── Visible columns — determined by payrollParams flags + visibleKeysState ─────
  const maxAllowanceIdx = Math.max(
    allowanceMasterList.length,
    rows.reduce((max, r) => {
      for (let i = 10; i >= 1; i--) {
        if (r[`Allowance${i}Name`]) return Math.max(max, i);
      }
      return max;
    }, 0)
  );
  const maxDeductionIdx = Math.max(
    deductionMasterList.length,
    rows.reduce((max, r) => {
      for (let i = 10; i >= 1; i--) {
        if (r[`Deduction${i}Name`]) return Math.max(max, i);
      }
      return max;
    }, 0)
  );

  const visCOLS = COLS.filter(col => {
    if (!visibleKeysState.has(col.key)) return false;
    if (col.paramKey && payrollParams && !payrollParams[col.paramKey]) return false;
    if (col.isAllowance && col.idx > maxAllowanceIdx) return false;
    if (col.isDeduction && col.idx > maxDeductionIdx) return false;
    return true;
  });
  visColsRef.current = visCOLS;

  const totalPages = Math.max(1, Math.ceil(rows.length / pageSize));
  const startIndex = (currentPage - 1) * pageSize;
  const endIndex = Math.min(rows.length, currentPage * pageSize);

  const currentPageRef = useRef(currentPage);
  useEffect(() => {
    currentPageRef.current = currentPage;
  }, [currentPage]);

  useEffect(() => {
    if (currentPage > totalPages) {
      setCurrentPage(totalPages);
    }
  }, [totalPages, currentPage]);

  useEffect(() => {
    const focusRow = focus.r;
    const pageNum = Math.floor(focusRow / pageSize) + 1;
    if (pageNum !== currentPageRef.current && pageNum >= 1 && pageNum <= totalPages) {
      setCurrentPage(pageNum);
    }
  }, [focus.r, pageSize, totalPages]);

  const handlePageChange = useCallback((newPage) => {
    const totalP = Math.max(1, Math.ceil(rowsRef.current.length / pageSize));
    if (newPage < 1 || newPage > totalP) return;
    setCurrentPage(newPage);
    const newRow = (newPage - 1) * pageSize;
    setFocus(f => ({ r: newRow, c: f.c }));
    setAnchor(a => ({ r: newRow, c: a.c }));
  }, [pageSize]);

  const handlePageSizeChange = useCallback((newSize) => {
    setPageSize(newSize);
    setCurrentPage(1);
    setFocus(f => ({ r: 0, c: f.c }));
    setAnchor(a => ({ r: 0, c: a.c }));
  }, []);

  // Clamp focus when visible columns change
  useEffect(() => {
    const maxC = visColsRef.current.length - 1;
    if (maxC < 0) return;
    setFocus(f => ({ ...f, c: Math.min(f.c, maxC) }));
    setAnchor(a => ({ ...a, c: Math.min(a.c, maxC) }));
    setEditMode(false);
  }, [visibleKeysState, payrollParams]); // eslint-disable-line react-hooks/exhaustive-deps

  // Close column picker on outside click
  useEffect(() => {
    if (!colPickerOpen) return;
    const handler = (e) => { if (colPickerRef.current && !colPickerRef.current.contains(e.target)) setColPickerOpen(false); };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [colPickerOpen]);

  const isColumnReadOnly = useCallback((col) => {
    if (!col) return true;
    if (col.readOnly) return true;
    if (col.key === "BasicSalary" || col.isAllowance) {
      return !(payrollParamsRef.current?.IsBasicEditable || payrollParamsRef.current?.IsDirectWagesEntry);
    }
    return false;
  }, []);

  const loadPayrollData = useCallback(() => {
    if (!isSessionReady) return;
    setIsLoading(true);
    const monthNum = MONTH_NAMES.indexOf(selectedMonth) + 1;
    const fd = new FormData();
    fd.append("Id", 0);
    fd.append("F_FirmMaster", sessionFirm.Id);
    fd.append("PayrollMonth", String(monthNum).padStart(2, "0"));
    fd.append("PayrollYear", selectedYear);
    
    Fn_GetReport(dispatch, () => {}, "data", "GetPayrollTransaction/0/token", { arguList: { id: 0, formData: fd } }, true)
      .then(res => {
        const records = Array.isArray(res) ? res : [];
        setSavedRecords(records);
        
        const savedEmpIds = new Set(records.map(r => String(r.F_EmployeeMaster)));
        const availableEmps = (employeesRef.current || []).filter(e => !savedEmpIds.has(String(e.Id)));
        
        const newEmpRowsData = [];
        const newEmpAllowances = [];
        const newEmpDeductions = [];
        
        availableEmps.forEach(emp => {
          let al = []; try { al = emp.AllowanceJson ? JSON.parse(emp.AllowanceJson) : []; } catch {}
          let dl = []; try { dl = emp.DeductionJson ? JSON.parse(emp.DeductionJson) : []; } catch {}
          let salList = []; try { salList = emp.SalaryJson ? JSON.parse(emp.SalaryJson) : []; } catch {}
          
          const salEntry = getEffectiveSalary(salList, selectedMonthRef.current, selectedYearRef.current);
          const monthDays = 31;
          const workingDays = computeWorkingDays(monthDays, payrollParamsRef.current);
          const basic = computeBasic(salEntry, workingDays, payrollParamsRef.current);
          const btEntry = basicTypeRef.current.find(bt => bt.Id === salEntry?.F_BasicTypeMaster);
          
          const baseRow = {
            ...emptyRow(),
            F_EmployeeMaster: emp.Id,
            EmployeeName: emp.Name || "",
            FatherName: emp.FatherName || "",
            Department: emp.DepartmentName || emp.Department || "",
            JoinDate: emp.JoinDate ? String(emp.JoinDate).substring(0, 10) : "",
            BasicSalary: parseFloat(salEntry?.BasicSalary) || 0,
            BasicTypeName: btEntry ? (btEntry.Name || btEntry.BasicType || "") : "",
            PFApplicable: emp.PFApplicable || false,
            ESIApplicable: emp.ESIApplicable || false,
            _isNew: true,
          };
          
          const computedRow = recomputeRow(baseRow, al, dl, pfRateRef.current, esiRateRef.current, bonusRateRef.current, gratuityRateRef.current, payrollParamsRef.current, salEntry, selectedMonthRef.current, selectedYearRef.current, allowanceMasterRef.current, deductionMasterRef.current);
          newEmpRowsData.push(computedRow);
          newEmpAllowances.push(al);
          newEmpDeductions.push(dl);
        });
        
        const newEmpRows = newEmpRowsData;
        
        const savedRows = [];
        const savedAllowances = [];
        const savedDeductions = [];

        records.forEach(r => {
          const emp = (employeesRef.current || []).find(e => String(e.Id) === String(r.F_EmployeeMaster));
          const al = reconstructAllowancesFromRecord(r, allowanceMasterRef.current);
          const dl = reconstructDeductionsFromRecord(r, deductionMasterRef.current);
          
          let salList = []; try { salList = emp?.SalaryJson ? JSON.parse(emp.SalaryJson) : []; } catch {}
          const salEntry = getEffectiveSalary(salList, selectedMonthRef.current, selectedYearRef.current);
          const monthDays = parseFloat(r.MonthDays) || 31;
          const workingDays = computeWorkingDays(monthDays, payrollParamsRef.current);
          const basic = computeBasic(salEntry, workingDays, payrollParamsRef.current);
          const btEntry = basicTypeRef.current.find(bt => bt.Id === (r.F_BasicTypeMaster || salEntry?.F_BasicTypeMaster));

          const baseRow = {
            ...emptyRow(),
            ...r,
            EmployeeName: emp?.Name || r.EmployeeName || "",
            FatherName: emp?.FatherName || r.FatherName || "",
            Department: emp?.DepartmentName || emp?.Department || r.Department || "",
            JoinDate: emp?.JoinDate ? String(emp.JoinDate).substring(0, 10) : (r.JoinDate || ""),
            BasicSalary: r.BasicSalary !== undefined ? parseFloat(r.BasicSalary) : (parseFloat(salEntry?.BasicSalary) || 0),
            BasicTypeName: btEntry ? (btEntry.Name || btEntry.BasicType || "") : (r.BasicTypeName || ""),
            PFApplicable: r.PFApplicable ?? emp?.PFApplicable ?? false,
            ESIApplicable: r.ESIApplicable ?? emp?.ESIApplicable ?? false,
            _isSaved: true
          };

          const computed = recomputeRow(
            baseRow, al, dl, pfRateRef.current, esiRateRef.current, bonusRateRef.current, gratuityRateRef.current,
            payrollParamsRef.current, { ...salEntry, BasicSalary: baseRow.BasicSalary, F_BasicTypeMaster: btEntry?.Id },
            selectedMonthRef.current, selectedYearRef.current, allowanceMasterRef.current, deductionMasterRef.current
          );

          savedRows.push(computed);
          savedAllowances.push(al);
          savedDeductions.push(dl);
        });

        const totalNeeded = Math.max(15, savedRows.length + newEmpRows.length + 5);
        const emptyRows = Array.from({ length: Math.max(0, totalNeeded - savedRows.length - newEmpRows.length) }, emptyRow);
        
        const allRows = [...savedRows, ...newEmpRows, ...emptyRows];
        setRows(allRows);
        
        const allAllowances = [
          ...savedAllowances,
          ...newEmpAllowances,
          ...Array(emptyRows.length).fill([])
        ];
        setRowAllowances(allAllowances);
        
        const allDeductions = [
          ...savedDeductions,
          ...newEmpDeductions,
          ...Array(emptyRows.length).fill([])
        ];
        setRowDeductions(allDeductions);
      })
      .catch(() => setSavedRecords([]))
      .finally(() => setIsLoading(false));
  }, [isSessionReady, selectedMonth, selectedYear, sessionFirm?.Id, dispatch]);

  // ── Load master data from API ─────────────────────────────────────────────
  useEffect(() => {
    const firmId = sessionFirm?.Id || JSON.parse(localStorage.getItem("selectedFirm") || "{}").Id || 0;
    if (!firmId) return;
    setIsLoading(true);

    const p1 = Fn_FillListData(dispatch, () => {}, "Data", `${API_WEB_URLS.MASTER}/0/token/EmployeeDataDynamic/0/${firmId}`)
      .then(data => setEmployees(data || [])).catch(() => {});

    const p2 = Fn_FillListData(dispatch, () => {}, "Data", `${API_WEB_URLS.MASTER}/0/token/PayrollParameter/TBL.F_FirmMaster/${firmId}`)
      .then(res => setPayrollParams(Array.isArray(res) && res.length > 0 ? res[0] : null)).catch(() => setPayrollParams(null));

    const p3 = Fn_FillListData(dispatch, () => {}, "Data", `${API_WEB_URLS.MASTER}/0/token/BasicTypeMaster/Id/0`)
      .then(data => setBasicTypeMaster(data || [])).catch(() => setBasicTypeMaster([]));

    const p4 = Fn_FillListData(dispatch, () => {}, "Data", `${API_WEB_URLS.MASTER}/0/token/AllowanceMaster/Id/0`)
      .then(data => setAllowanceMasterList(data || [])).catch(() => setAllowanceMasterList([]));

    const p5 = Fn_FillListData(dispatch, () => {}, "Data", `${API_WEB_URLS.MASTER}/0/token/DeductionMaster/Id/0`)
      .then(data => setDeductionMasterList(data || [])).catch(() => setDeductionMasterList([]));

    const p5_2 = Fn_FillListData(dispatch, () => {}, "Data", `${API_WEB_URLS.MASTER}/0/token/LeaveMaster/TBL.F_FirmMaster/${firmId}`)
      .then(data => setLeaveMasterList(data || [])).catch(() => setLeaveMasterList([]));

    const p6 = Fn_FillListData(dispatch, () => {}, "Data", `${API_WEB_URLS.MASTER}/0/token/PfParamFetch/TBL.F_FirmMaster/${firmId}`)
      .then(res => { const s = (res || []).sort((a, b) => new Date(a.PFDate) - new Date(b.PFDate)); setPfRate(s.length > 0 ? s[s.length - 1] : null); }).catch(() => setPfRate(null));

    const p7 = Fn_FillListData(dispatch, () => {}, "Data", `${API_WEB_URLS.MASTER}/0/token/EsiParamFetch/TBL.F_FirmMaster/${firmId}`)
      .then(res => { const s = (res || []).sort((a, b) => new Date(a.ESIDate) - new Date(b.ESIDate)); setEsiRate(s.length > 0 ? s[s.length - 1] : null); }).catch(() => setEsiRate(null));

    const p8 = Fn_FillListData(dispatch, () => {}, "Data", `${API_WEB_URLS.MASTER}/0/token/BonusParamFetch/TBL.F_FirmMaster/${firmId}`)
      .then(res => { const s = (res || []).sort((a, b) => new Date(a.BonusDate) - new Date(b.BonusDate)); setBonusRate(s.length > 0 ? s[s.length - 1] : null); }).catch(() => setBonusRate(null));

    const p9 = Fn_FillListData(dispatch, () => {}, "Data", `${API_WEB_URLS.MASTER}/0/token/GratuityParamFetch/TBL.F_FirmMaster/${firmId}`)
      .then(res => { const s = (res || []).sort((a, b) => new Date(a.GratuityDate) - new Date(b.GratuityDate)); setGratuityRate(s.length > 0 ? s[s.length - 1] : null); }).catch(() => setGratuityRate(null));

    Promise.all([p1, p2, p3, p4, p5, p5_2, p6, p7, p8, p9]).finally(() => {
      setIsLoading(false);
    });
  }, [dispatch, sessionFirm?.Id]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Load saved payroll records + available employees ──────────────────────
  useEffect(() => {
    loadPayrollData();
  }, [loadPayrollData, employees, allowanceMasterList, deductionMasterList]);

  // Auto-focus first employee cell when loading completes
  useEffect(() => {
    if (!isLoading && isSessionReady && rows.length > 0) {
      const emptyRowIdx = rows.findIndex(r => !r.F_EmployeeMaster);
      const targetRow = emptyRowIdx >= 0 ? emptyRowIdx : 0;
      setFocus({ r: targetRow, c: 0 });
      setAnchor({ r: targetRow, c: 0 });
      setEditMode(true);
    }
  }, [isLoading, isSessionReady]);

  // ── Scroll focused cell into view ─────────────────────────────────────────
  useEffect(() => {
    const container = gridRef.current; const cell = focusedCellRef.current;
    if (!container || !cell) return;
    const cRect = container.getBoundingClientRect(); const eRect = cell.getBoundingClientRect();
    const stickyLeft = 40;
    if (eRect.left < cRect.left + stickyLeft) container.scrollLeft -= cRect.left + stickyLeft - eRect.left;
    else if (eRect.right > cRect.right)        container.scrollLeft += eRect.right - cRect.right;
    const stickyTop = 56;
    if (eRect.top < cRect.top + stickyTop) container.scrollTop -= cRect.top + stickyTop - eRect.top;
    else if (eRect.bottom > cRect.bottom)   container.scrollTop += eRect.bottom - cRect.bottom;
  }, [focus]);

  // ── Focus input when entering edit mode ──────────────────────────────────
  useEffect(() => {
    if (editMode) {
      const el = inputRef.current; if (!el) return;
      el.focus();
      const supportsSelection = el.setSelectionRange && el.tagName !== "SELECT" && el.type !== "number";
      if (supportsSelection) { try { el.setSelectionRange(el.value?.length ?? 0, el.value?.length ?? 0); } catch (_) {} }
      if (openDropdownRef.current && el.tagName === "SELECT") { openDropdownRef.current = false; try { el.showPicker(); } catch (_) {} }
    } else { gridRef.current?.focus(); }
  }, [focus, editMode]);

  // ── Sync local editValue when active cell changes or edit mode starts ─────
  useEffect(() => {
    if (editMode) {
      const { r, c } = focus;
      const col = visColsRef.current[c];
      if (col) {
        const val = rowsRef.current[r]?.[col.key] ?? "";
        setLocalEditValue(String(val));
      }
    }
  }, [focus, editMode]);

  // ── Derived ───────────────────────────────────────────────────────────────
  const selRange = getSelRange(focus, anchor);

  const ensureRows = useCallback((needed) => {
    setRows(prev => prev.length >= needed ? prev : [...prev, ...Array.from({ length: needed - prev.length }, emptyRow)]);
    setRowAllowances(prev => { if (prev.length >= needed) return prev; const n = [...prev]; while (n.length < needed) n.push([]); return n; });
    setRowDeductions(prev => { if (prev.length >= needed) return prev; const n = [...prev]; while (n.length < needed) n.push([]); return n; });
  }, []);

  const addNewRow = useCallback(() => {
    setRows(prev => {
      const next = [...prev, emptyRow()];
      const newIdx = next.length - 1;
      setTimeout(() => {
        setFocus({ r: newIdx, c: 0 });
        setAnchor({ r: newIdx, c: 0 });
        setEditMode(true);
      }, 0);
      return next;
    });
    setRowAllowances(p => [...p, []]);
    setRowDeductions(p => [...p, []]);
  }, []);

  // ── Handle employee selection: load allowances, deductions, salary ────────
  const handleEmployeeSelect = useCallback((rowIndex, empId) => {
    if (!empId) {
      setRows(prev => prev.map((row, i) => i === rowIndex ? { ...emptyRow(), MonthDays: row.MonthDays } : row));
      setRowAllowances(prev => prev.map((al, i) => i === rowIndex ? [] : al));
      setRowDeductions(prev => prev.map((dl, i) => i === rowIndex ? [] : dl));
      return;
    }
    const emps = employeesRef.current;
    const found = emps.find(e => String(e.Id) === String(empId));
    if (!found) return;

    let al = []; try { al = found.AllowanceJson ? JSON.parse(found.AllowanceJson) : []; } catch {}
    let dl = []; try { dl = found.DeductionJson ? JSON.parse(found.DeductionJson) : []; } catch {}
    let salList = []; try { salList = found.SalaryJson ? JSON.parse(found.SalaryJson) : []; } catch {}

    setRowAllowances(prev => { const next = [...prev]; while (next.length <= rowIndex) next.push([]); next[rowIndex] = al; return next; });
    setRowDeductions(prev => { const next = [...prev]; while (next.length <= rowIndex) next.push([]); next[rowIndex] = dl; return next; });

    setRows(prev => {
      const next      = [...prev];
      const row       = { ...next[rowIndex] };
      const mon       = selectedMonthRef.current;
      const yr        = selectedYearRef.current;
      const salEntry  = getEffectiveSalary(salList, mon, yr);
      const monthDays = n(row.MonthDays) || 31;
      const basic     = computeBasic(salEntry, computeWorkingDays(monthDays, payrollParamsRef.current), payrollParamsRef.current);
      const btEntry   = basicTypeRef.current.find(bt => bt.Id === salEntry?.F_BasicTypeMaster);

      const updated = {
        ...row,
        F_EmployeeMaster: empId,
        EmployeeName:     found.Name             || "",
        FatherName:       found.FatherName       || "",
        Department:       found.DepartmentName   || found.Department || "",
        JoinDate:         found.JoinDate ? String(found.JoinDate).substring(0, 10) : "",
        // Show raw rate from master: 500 for daily, 13000 for monthly — as-is
        BasicSalary:      parseFloat(salEntry?.BasicSalary) || 0,
        BasicTypeName:    btEntry ? (btEntry.Name || btEntry.BasicType || "") : "",
        PFApplicable:     found.PFApplicable  || false,
        ESIApplicable:    found.ESIApplicable || false,
      };
      next[rowIndex] = recomputeRow(updated, al, dl, pfRateRef.current, esiRateRef.current, bonusRateRef.current, gratuityRateRef.current, payrollParamsRef.current, salEntry, mon, yr, allowanceMasterRef.current, deductionMasterRef.current);
      return next;
    });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Set a cell value, then recompute the row ──────────────────────────────
  const setCell = useCallback((r, key, val) => {
    if (key === "F_EmployeeMaster") { handleEmployeeSelect(r, val); return; }
    setRows(prev => {
      const next    = [...prev];
      const numericVal = parseFloat(val) || 0;
      
      if (key === "WorkingDays" && numericVal > (parseFloat(next[r].MonthDays) || 0)) {
        toast.error(`Working Days (${numericVal}) cannot exceed Month Days (${next[r].MonthDays || 31})`);
        next[r] = { ...next[r], WorkingDays: 0 };
      } else if (key === "MonthDays" && numericVal < (parseFloat(next[r].WorkingDays) || 0)) {
        toast.error(`Month Days (${numericVal}) cannot be less than Working Days (${next[r].WorkingDays || 0})`);
        next[r] = { ...next[r], WorkingDays: 0, MonthDays: numericVal };
      } else {
        next[r] = { ...next[r], [key]: val };
      }

      const row     = next[r];
      const al      = rowAllowancesRef.current[r] || [];
      const dl      = rowDeductionsRef.current[r]  || [];
      const emp     = employeesRef.current.find(e => String(e.Id) === String(row.F_EmployeeMaster));
      let salEntry  = null;
      if (emp) { try { salEntry = getEffectiveSalary(JSON.parse(emp.SalaryJson || "[]"), selectedMonthRef.current, selectedYearRef.current); } catch {} }
      next[r] = recomputeRow(row, al, dl, pfRateRef.current, esiRateRef.current, bonusRateRef.current, gratuityRateRef.current, payrollParamsRef.current, salEntry, selectedMonthRef.current, selectedYearRef.current, allowanceMasterRef.current, deductionMasterRef.current);
      return next;
    });
  }, [handleEmployeeSelect]);

  // ── Recalculate all rows (after rate/param changes) ───────────────────────
  const recalcAll = useCallback(() => {
    setRows(prev => prev.map((row, i) => {
      const al     = rowAllowancesRef.current[i] || [];
      const dl     = rowDeductionsRef.current[i]  || [];
      const emp    = employeesRef.current.find(e => String(e.Id) === String(row.F_EmployeeMaster));
      let salEntry = null;
      if (emp) { try { salEntry = getEffectiveSalary(JSON.parse(emp.SalaryJson || "[]"), selectedMonthRef.current, selectedYearRef.current); } catch {} }
      return recomputeRow(row, al, dl, pfRateRef.current, esiRateRef.current, bonusRateRef.current, gratuityRateRef.current, payrollParamsRef.current, salEntry, selectedMonthRef.current, selectedYearRef.current);
    }));
  }, []);

  // Recalc when rates or params load
  useEffect(() => { recalcAll(); }, [pfRate, esiRate, bonusRate, gratuityRate, payrollParams]); // eslint-disable-line react-hooks/exhaustive-deps
  // Recalc when session month/year changes
  useEffect(() => { recalcAll(); }, [selectedMonth, selectedYear]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Navigation helpers ────────────────────────────────────────────────────
  const gotoCell = useCallback((r, c, extendSel = false) => {
    const nr = Math.max(0, r);
    const nc = Math.max(0, Math.min(c, visColsRef.current.length - 1));
    if (nr >= rowsRef.current.length) ensureRows(nr + 1);
    if (!extendSel) setAnchor({ r: nr, c: nc });
    setFocus({ r: nr, c: nc });
    setEditMode(false);
  }, [ensureRows]);

  const gotoNextEditableCell = useCallback((startR, startC, direction = 1) => {
    let currR = startR;
    let currC = startC;
    const colCount = visColsRef.current.length;
    if (colCount === 0) return;
    
    while (true) {
      currC += direction;
      if (currC >= colCount) {
        currC = 0;
        currR += 1;
      } else if (currC < 0) {
        currC = colCount - 1;
        currR -= 1;
      }
      
      // If out of bounds, stop
      if (currR < 0 || currR >= rowsRef.current.length) {
        break;
      }
      
      const col = visColsRef.current[currC];
      if (!isColumnReadOnly(col)) {
        setFocus({ r: currR, c: currC });
        setAnchor({ r: currR, c: currC });
        setEditMode(col.type === "text" || col.type === "number" || col.type === "select" || col.type === "date");
        return;
      }
    }
  }, [isColumnReadOnly]);

  const enterEditMode = useCallback((r, c, overwriteChar = null) => {
    const col = visColsRef.current[c];
    if (!col || isColumnReadOnly(col)) return;
    editOrigRef.current     = rowsRef.current[r]?.[col.key] ?? "";
    editModeTypeRef.current = overwriteChar === null ? "f2" : "typing";
    if (overwriteChar !== null && (col.type === "text" || col.type === "number")) {
      setLocalEditValue(overwriteChar);
    } else {
      const val = rowsRef.current[r]?.[col.key] ?? "";
      setLocalEditValue(String(val));
    }
    setEditMode(true);
  }, [isColumnReadOnly]);

  // ── Copy / Paste / Clear ──────────────────────────────────────────────────
  const doCopy = useCallback(() => {
    const { r1, r2, c1, c2 } = getSelRange(focusRef.current, anchorRef.current);
    const lines = [];
    for (let ri = r1; ri <= r2; ri++) {
      const cells = [];
      for (let ci = c1; ci <= c2; ci++) cells.push(String(rowsRef.current[ri]?.[visColsRef.current[ci].key] ?? ""));
      lines.push(cells.join("\t"));
    }
    navigator.clipboard?.writeText(lines.join("\n"));
  }, []);

  const doPaste = useCallback((text, startR, startC) => {
    if (!text) return;
    const pasted = text.replace(/\r/g, "").trimEnd().split("\n").map(l => l.split("\t"));
    setRows(prev => {
      const next = [...prev];
      while (next.length < startR + pasted.length) next.push(emptyRow());
      pasted.forEach((cols, dr) => cols.forEach((val, dc) => {
        const tc = startC + dc;
        if (tc < visColsRef.current.length) {
          const col = visColsRef.current[tc];
          if (!isColumnReadOnly(col)) next[startR + dr] = { ...next[startR + dr], [col.key]: val.trim() };
        }
      }));
      return next;
    });
  }, [isColumnReadOnly]);

  const clearSel = useCallback(() => {
    const { r1, r2, c1, c2 } = getSelRange(focusRef.current, anchorRef.current);
    setRows(prev => {
      const next = prev.map(row => ({ ...row }));
      for (let ri = r1; ri <= r2; ri++)
        for (let ci = c1; ci <= c2; ci++) {
          const col = visColsRef.current[ci];
          if (col && !isColumnReadOnly(col)) next[ri][col.key] = "";
        }
      return next;
    });
  }, [isColumnReadOnly]);

  const selectAll = useCallback(() => { setAnchor({ r: 0, c: 0 }); setFocus({ r: rowsRef.current.length - 1, c: visColsRef.current.length - 1 }); }, []);

  // ── Keyboard handler ──────────────────────────────────────────────────────
  const handleKey = useCallback((e) => {
    const { r, c } = focusRef.current;
    const col = visColsRef.current[c];
    if (!col) return;
    const isTextLike = col.type === "text" || col.type === "number" || col.type === "date";
    const shift = e.shiftKey; const ctrl = e.ctrlKey || e.metaKey; const inEdit = editModeRef.current;
    if (ctrl) {
      if (e.key.toLowerCase() === "c") { doCopy(); return; }
      if (e.key.toLowerCase() === "v") { e.preventDefault(); navigator.clipboard?.readText().then(t => doPaste(t, r, c)); return; }
      if (e.key.toLowerCase() === "a") { e.preventDefault(); selectAll(); return; }
      return;
    }
    if (inEdit) {
      const isF2Mode = editModeTypeRef.current === "f2";
      switch (e.key) {
        case "Escape": e.preventDefault(); setCell(r, col.key, editOrigRef.current); setEditMode(false); break;
        case "Enter":  e.preventDefault(); e.stopPropagation(); if (col.type !== "select") setCell(r, col.key, editValueRef.current); setEditMode(false); gotoNextEditableCell(r, c, shift ? -1 : 1); break;
        case "Tab":    e.preventDefault(); e.stopPropagation(); if (col.type !== "select") setCell(r, col.key, editValueRef.current); setEditMode(false); gotoNextEditableCell(r, c, shift ? -1 : 1); break;
        case "ArrowUp":    if (isF2Mode) break; e.preventDefault(); e.stopPropagation(); if (col.type !== "select") setCell(r, col.key, editValueRef.current); setEditMode(false); gotoCell(r - 1, c); break;
        case "ArrowDown":  if (isF2Mode) break; e.preventDefault(); e.stopPropagation(); if (col.type !== "select") setCell(r, col.key, editValueRef.current); setEditMode(false); gotoCell(r + 1, c); break;
        case "ArrowLeft":  if (isF2Mode) break; e.preventDefault(); e.stopPropagation(); if (col.type !== "select") setCell(r, col.key, editValueRef.current); setEditMode(false); gotoCell(r, c - 1); break;
        case "ArrowRight": if (isF2Mode) break; e.preventDefault(); e.stopPropagation(); if (col.type !== "select") setCell(r, col.key, editValueRef.current); setEditMode(false); gotoCell(r, c + 1); break;
        default: break;
      }
      return;
    }
    switch (e.key) {
      case "Tab":        e.preventDefault(); gotoNextEditableCell(r, c, shift ? -1 : 1); break;
      case "Enter":      e.preventDefault(); e.stopPropagation(); gotoNextEditableCell(r, c, shift ? -1 : 1); break;
      case "F2":         e.preventDefault(); enterEditMode(r, c, null); break;
      case "ArrowUp":    e.preventDefault(); e.stopPropagation(); gotoCell(r - 1, c, shift); break;
      case "ArrowDown":  e.preventDefault(); e.stopPropagation(); gotoCell(r + 1, c, shift); break;
      case "ArrowLeft":  e.preventDefault(); e.stopPropagation(); gotoCell(r, c - 1, shift); break;
      case "ArrowRight": e.preventDefault(); e.stopPropagation(); gotoCell(r, c + 1, shift); break;
      case "Delete": case "Backspace": e.preventDefault(); clearSel(); break;
      case " ": if (col.type === "select") { e.preventDefault(); openDropdownRef.current = true; enterEditMode(r, c, null); } break;
      default: if (isTextLike && e.key.length === 1 && !isColumnReadOnly(col)) { e.preventDefault(); enterEditMode(r, c, e.key); } break;
    }
  }, [gotoCell, gotoNextEditableCell, enterEditMode, setCell, doCopy, doPaste, clearSel, selectAll, isColumnReadOnly]);

  const handleGridKeyDown = useCallback((e) => {
    if (e.target !== gridRef.current) {
      const isF2Mode = editModeTypeRef.current === "f2";
      if (!isF2Mode && ["ArrowUp","ArrowDown","ArrowLeft","ArrowRight"].includes(e.key)) e.preventDefault();
      return;
    }
    handleKey(e);
  }, [handleKey]);

  const handleCellClick = useCallback((r, c, e) => {
    if (e.shiftKey) { setEditMode(false); setFocus({ r, c }); }
    else { gotoCell(r, c); }
  }, [gotoCell]);

  // ── Delete row ────────────────────────────────────────────────────────────
  const delRow = (idx) => {
    setEditMode(false);
    if (rows.length === 1) {
      setRows([emptyRow()]); setRowAllowances([[]]); setRowDeductions([[]]);
      setFocus({ r: 0, c: 0 }); setAnchor({ r: 0, c: 0 }); return;
    }
    setRows(prev => prev.filter((_, i) => i !== idx));
    setRowAllowances(prev => prev.filter((_, i) => i !== idx));
    setRowDeductions(prev => prev.filter((_, i) => i !== idx));
    const fix = (pos) => ({ ...pos, r: Math.max(0, pos.r >= idx ? pos.r - 1 : pos.r) });
    setFocus(fix); setAnchor(fix);
  };

  // ── Save ──────────────────────────────────────────────────────────────────
  const handleSave = async () => {
    if (!isSessionReady) { toast.error("Please select Firm, Month and Year from Home first."); return; }
    // Filter rows that have WorkingDays > 0
    const validRows = rows.filter(r => r.F_EmployeeMaster && parseFloat(r.WorkingDays) > 0);
    if (!validRows.length) { toast.error("Please enter WorkingDays for at least one employee."); return; }
    setIsLoading(true);
    let savedCount = 0; let failCount = 0;
    for (const row of validRows) {
      try {
        const fd = new FormData();
        const dateKeys = new Set([
          "LeaveFrom1", "LeaveTo1", "LeaveFrom2", "LeaveTo2", "LeaveFrom3", "LeaveTo3",
          "AbsentFrom1", "AbsentTo1", "AbsentFrom2", "AbsentTo2", "AbsentFrom3", "AbsentTo3"
        ]);
        fd.append("F_FirmMaster", sessionFirm?.Id || 0);
        fd.append("PayrollYear", selectedYear || 0);
        fd.append("PayrollMonth", selectedMonth || "");
        fd.append("Month", selectedMonth || "");
        fd.append("Year", selectedYear || 0);

        Object.keys(emptyRow()).forEach(k => {
          if (k === "Id" || k === "Month" || k === "Year") return;
          if (dateKeys.has(k)) {
            if (row[k]) fd.append(k, formatDateForSave(row[k]));
          } else {
            fd.append(k, row[k] ?? 0);
          }
        });
        fd.append("IsBasicEditable", payrollParams?.IsBasicEditable ? true : false);
        fd.append("UserId", 0);

        // ID logic:
        // - If _isSaved = true: use actual ID (editing existing record)
        // - Otherwise: send 0 (new record)
        const recordId = (row._isSaved && row.Id) ? row.Id : 0;
        const res = await Fn_AddEditData(dispatch, () => {}, { arguList: { id: recordId, formData: fd } }, "PayrollTransaction/0/token", true, "memberid", navigate, "#");
        if (res && res?.id > 0) savedCount++;
        else failCount++;
      } catch (err) {
        console.error("Save row error:", err);
        failCount++;
      }
    }
    setIsLoading(false);
    if (savedCount > 0) { toast.success(`${savedCount} payroll record(s) saved!`, { autoClose: 3000 }); }
    if (failCount  > 0) { toast.error(`${failCount} record(s) failed.`,           { autoClose: 4000 }); }
    // Reload saved records + available employees using the unified loader
    loadPayrollData();
  };

  // ── Cell display value ────────────────────────────────────────────────────
  const displayVal = (col, val) => {
    if (col.type === "select" && col.optKey === "EmployeeMaster") {
      const emp = employees.find(e => String(e.Id) === String(val));
      return emp ? emp.Name : "";
    }
    if (col.type === "number") return val !== "" && val !== undefined && val !== null ? String(val) : "";
    return val ?? "";
  };

  // ── Group header cells ────────────────────────────────────────────────────
  const groupHeaderCells = (() => {
    const cells = [{ label: "", colSpan: 1, color: "#cbd5e1" }];
    COL_GROUPS.forEach(grp => {
      const startIdx = COLS.findIndex(c => c.key === grp.startKey);
      const endIdx   = COLS.findIndex(c => c.key === grp.endKey);
      if (startIdx < 0 || endIdx < 0) return;
      const visCount = COLS.slice(startIdx, endIdx + 1).filter(col => {
        if (!visibleKeysState.has(col.key)) return false;
        if (col.paramKey && payrollParams && !payrollParams[col.paramKey]) return false;
        return true;
      }).length;
      if (visCount === 0) return;
      cells.push({ label: grp.label, colSpan: visCount, color: grp.color });
    });
    cells.push({ label: "", colSpan: 1, color: "#cbd5e1" });
    return cells;
  })();

  const filledCount = rows.filter(r => r.F_EmployeeMaster).length;
  const isMultiSel  = selRange.r2 > selRange.r1 || selRange.c2 > selRange.c1;

  // ── Get allowance/deduction breakdown for tooltip ──────────────────────
  const getAllowanceBreakdown = useCallback((rowIdx) => {
    const row = rows[rowIdx];
    const allowances = rowAllowances[rowIdx] || [];
    if (!allowances.length || !allowanceMasterList?.length) return [];
    
    return allowances.map(a => {
      const name = allowanceMasterList.find(m => String(m.Id) === String(a.F_AllowanceMaster))?.Name || `Allow ${a.F_AllowanceMaster}`;
      const monthDays = row.MonthDays || 26;
      const paidDays = row.PaidDay || 0;
      const basicSal = row.BasicSalary || 0;
      let computed = 0;
      
      if (a.AllowanceType === 1) {
        computed = n(a.Amount);  // Fixed
      } else if (a.AllowanceType === 2) {
        computed = (n(a.Percentage) / 100) * basicSal;  // % of Basic
      } else if (a.AllowanceType === 3) {
        computed = (n(a.Amount) / monthDays) * paidDays;  // Per Day
      }
      return { name, amount: computed };
    });
  }, [rows, rowAllowances, allowanceMasterList]);

  const getDeductionBreakdown = useCallback((rowIdx) => {
    const row = rows[rowIdx];
    const deductions = rowDeductions[rowIdx] || [];
    if (!deductions.length || !deductionMasterList?.length) return [];
    
    return deductions.map(d => {
      const name = deductionMasterList.find(m => String(m.Id) === String(d.F_DeductionMaster))?.Name || `Deduct ${d.F_DeductionMaster}`;
      const monthDays = row.MonthDays || 26;
      const paidDays = row.PaidDay || 0;
      const basicSal = row.BasicSalary || 0;
      let computed = 0;
      
      if (d.DeductionType === 1) {
        computed = n(d.Amount);  // Fixed
      } else if (d.DeductionType === 2) {
        computed = (n(d.Percentage) / 100) * basicSal;  // % of Basic
      } else if (d.DeductionType === 3) {
        computed = (n(d.Amount) / monthDays) * paidDays;  // Per Day
      }
      return { name, amount: computed };
    });
  }, [rows, rowDeductions, deductionMasterList]);

  // ─────────────────────────────────────────────────────────────────────────
  return (
    <div style={{ display: "flex", flexDirection: "column", height: "calc(100vh - 80px)", overflow: "hidden" }}>
      {/* ─── Focus highlight and animation styles ─── */}
      <style>{`
        @keyframes py-spin {
          0%   { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
        @keyframes py-pulse {
          0%, 100% { opacity: 1; }
          50%       { opacity: 0.5; }
        }
        @keyframes py-fade-in {
          from { opacity: 0; }
          to   { opacity: 1; }
        }
      `}</style>

      <PageTitle activeMenu="Multi Payroll Entry" motherMenu="Transaction" pageContent="Multi Payroll Entry" />

      {/* ─── Full-Page Loader Overlay ─── */}
      {isLoading && (
        <div style={{
          position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
          background: 'rgba(255,255,255,0.72)',
          backdropFilter: 'blur(3px)',
          zIndex: 9999,
          display: 'flex', flexDirection: 'column',
          alignItems: 'center', justifyContent: 'center',
          animation: 'py-fade-in 0.2s ease',
        }}>
          {/* Spinner */}
          <div style={{
            width: 52, height: 52,
            border: '5px solid #E9ECEF',
            borderTop: '5px solid #1a5276',
            borderRadius: '50%',
            animation: 'py-spin 0.75s linear infinite',
            marginBottom: 16,
            boxShadow: '0 4px 16px rgba(26,82,118,0.18)',
          }} />
          <div style={{
            fontSize: 14, fontWeight: 700, color: '#1a5276',
            letterSpacing: '0.3px',
            animation: 'py-pulse 1.4s ease-in-out infinite',
          }}>Loading...</div>
          <div style={{ fontSize: 12, color: '#67748E', marginTop: 4 }}>Please wait</div>
        </div>
      )}

      {/* ─── Session Banner ─── */}
      {isSessionReady ? (
        <div style={{ display: "flex", alignItems: "center", gap: "10px", background: "#1a5276", color: "#fff", padding: "5px 14px", fontSize: "12px", fontWeight: 600, flexShrink: 0 }}>
          <span style={{ color: "#fff" }}>Firm: {sessionFirm.Name}</span>
          <span style={{ opacity: 0.5, color: "#fff" }}>|</span>
          <span style={{ color: "#fff" }}>Month: {selectedMonth}</span>
          <span style={{ opacity: 0.5, color: "#fff" }}>|</span>
          <span style={{ color: "#fff" }}>Year: {selectedYear}</span>
        </div>
      ) : (
        <div style={{ background: "#fdf3cd", border: "1px solid #f0ad4e", padding: "8px 16px", fontSize: "13px", color: "#856404", flexShrink: 0, textAlign: "center" }}>
          ⚠ Please go to <strong>Home</strong> and select Firm, Month &amp; Year to enable Multi Payroll Entry.
        </div>
      )}

      {/* ─── Toolbar ─── */}
      <div style={{ display: "flex", alignItems: "center", flexWrap: "wrap", gap: "8px", padding: "6px 12px", background: "#e8f5e9", borderBottom: "2px solid #217346", flexShrink: 0, opacity: isSessionReady ? 1 : 0.5, pointerEvents: isSessionReady ? "auto" : "none" }}>
        <span style={{ fontSize: "14px", fontWeight: 700, color: "#217346", marginRight: "4px" }}>
          Multi Payroll Entry
        </span>

        <button className="btn btn-sm btn-outline-success" style={{ fontSize: "12px", padding: "2px 10px" }} onClick={addNewRow}>
          + Add Row
        </button>

        <button className="btn btn-sm btn-outline-warning" style={{ fontSize: "12px", padding: "2px 10px" }} onClick={recalcAll}>
          ⟳ Recalculate All
        </button>

        <button className="btn btn-sm btn-outline-danger" style={{ fontSize: "12px", padding: "2px 10px" }}
          onClick={() => {
            if (window.confirm("Clear all rows?")) {
              setRows(Array.from({ length: 10 }, emptyRow));
              setRowAllowances(Array.from({ length: 10 }, () => []));
              setRowDeductions(Array.from({ length: 10 }, () => []));
              setFocus({ r: 0, c: 0 }); setAnchor({ r: 0, c: 0 }); setEditMode(false);
              setCurrentPage(1);
            }
          }}>
          ✕ Clear All
        </button>

        <button className="btn btn-sm btn-outline-info" style={{ fontSize: "12px", padding: "2px 10px" }} onClick={doCopy} title="Copy selected cells (Ctrl+C)">
          📋 Copy
        </button>

        <button className="btn btn-sm btn-outline-info" style={{ fontSize: "12px", padding: "2px 10px" }} onClick={() => navigator.clipboard?.readText().then(t => doPaste(t, focus.r, focus.c))} title="Paste from clipboard (Ctrl+V)">
          📌 Paste
        </button>

        <button className="btn btn-sm btn-outline-info" style={{ fontSize: "12px", padding: "2px 10px" }} onClick={clearSel} title="Clear selected cells (Delete)">
          🗑 Clear Sel
        </button>

        <button className="btn btn-sm btn-outline-info" style={{ fontSize: "12px", padding: "2px 10px" }} onClick={selectAll} title="Select all cells (Ctrl+A)">
          ✔ Select All
        </button>

        <button className="btn btn-sm btn-outline-secondary" style={{ fontSize: "12px", padding: "2px 10px" }} onClick={() => navigate("/Payroll")}>
          ← Back
        </button>

        {/* Column picker */}
        <div ref={colPickerRef} style={{ position: "relative" }}>
          <button className="btn btn-sm btn-outline-primary" style={{ fontSize: "12px", padding: "2px 10px" }} onClick={() => setColPickerOpen(p => !p)}>
            ⊞ Columns ({visCOLS.length})
          </button>
          {colPickerOpen && (
            <div style={{ position: "absolute", top: "calc(100% + 6px)", left: 0, zIndex: 200, background: "#fff", border: "1px solid #d0d0d0", borderRadius: "6px", boxShadow: "0 4px 18px rgba(0,0,0,0.18)", minWidth: "240px", maxHeight: "70vh", overflowY: "auto" }}>
              <div style={{ padding: "6px 12px", borderBottom: "1px solid #eee", position: "sticky", top: 0, background: "#fff", zIndex: 1, display: "flex", gap: "6px", alignItems: "center" }}>
                <span style={{ fontSize: "11px", fontWeight: 700, flex: 1 }}>Column Visibility</span>
                <button onClick={() => setVisibleKeysState(new Set(COLS.map(c => c.key)))} style={{ fontSize: "10px", padding: "1px 8px", border: "1px solid #217346", borderRadius: "3px", background: "#e8f5e9", color: "#217346", cursor: "pointer" }}>All</button>
                <button onClick={() => setVisibleKeysState(new Set())}                     style={{ fontSize: "10px", padding: "1px 8px", border: "1px solid #dc3545", borderRadius: "3px", background: "#fff0f0", color: "#dc3545", cursor: "pointer" }}>None</button>
              </div>
              {COL_GROUPS.map(grp => {
                const startIdx = COLS.findIndex(c => c.key === grp.startKey);
                const endIdx   = COLS.findIndex(c => c.key === grp.endKey);
                if (startIdx < 0 || endIdx < 0) return null;
                const grpCols = COLS.slice(startIdx, endIdx + 1);
                return (
                  <div key={grp.label}>
                    <div style={{ padding: "3px 12px", background: grp.color, color: "#fff", fontSize: "11px", fontWeight: 700, display: "flex", justifyContent: "space-between" }}>
                      <span>{grp.label}</span>
                      <span style={{ opacity: 0.8, fontSize: "10px" }}>{grpCols.filter(c => visibleKeysState.has(c.key)).length}/{grpCols.length}</span>
                    </div>
                    {grpCols.map(col => (
                      <label key={col.key} style={{ display: "flex", alignItems: "center", gap: "7px", padding: "3px 16px", cursor: "pointer", fontSize: "12px", userSelect: "none", color: "#333" }}>
                        <input type="checkbox" checked={visibleKeysState.has(col.key)} onChange={e => setVisibleKeysState(prev => { const next = new Set(prev); e.target.checked ? next.add(col.key) : next.delete(col.key); return next; })} />
                        {col.label}
                        {col.readOnly && <span style={{ color: "#999", fontSize: "10px" }}>(auto)</span>}
                        {col.paramKey && <span style={{ color: "#888", fontSize: "10px" }}>[{col.paramKey}]</span>}
                      </label>
                    ))}
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Keyboard hints */}
        <span style={{ fontSize: "11px", color: "#444", marginLeft: "4px" }}>
          <kbd style={KBD}>↑↓←→</kbd> Nav &nbsp;
          <kbd style={KBD}>F2</kbd>/<kbd style={KBD}>DblClick</kbd> Edit &nbsp;
          <kbd style={KBD}>Esc</kbd> Restore &nbsp;
          <kbd style={KBD}>Shift+Arrow</kbd> Select &nbsp;
          <kbd style={KBD}>Ctrl+C/V</kbd> Copy/Paste
        </span>

        <div style={{ marginLeft: "auto", display: "flex", alignItems: "center", gap: "10px" }}>
          <span style={{ fontSize: "12px", fontWeight: 700, color: "#217346" }}>{filledCount}/{rows.length} rows filled</span>
          <button className="btn btn-sm btn-success" style={{ padding: "3px 14px", fontSize: "13px" }} onClick={handleSave} disabled={isLoading || !isSessionReady}>
            {isLoading ? "Saving..." : "Save All"}
          </button>
        </div>
      </div>

      {/* ─── Grid ─── */}
      <div
        ref={gridRef}
        tabIndex={0}
        onKeyDown={handleGridKeyDown}
        style={{ flex: 1, overflowX: "auto", overflowY: "auto", outline: "none", background: "#fff", opacity: isSessionReady ? 1 : 0.45, pointerEvents: isSessionReady ? "auto" : "none" }}
      >
        <table style={{ borderCollapse: "collapse", tableLayout: "fixed", fontSize: "13px", userSelect: "none" }}>
          <thead>
            {/* Column name row */}
            <tr>
              <th style={TH_NUM}>#</th>
              {visCOLS.map((col, ci) => {
                let label = (col.labelParamKey && payrollParams?.[col.labelParamKey]) ? payrollParams[col.labelParamKey] : col.label;
                if (col.key === "CL") {
                  label = leaveMasterList[0]?.Name || "CL";
                } else if (col.key === "ML") {
                  label = leaveMasterList[1]?.Name || "ML";
                } else if (col.isAllowance) {
                  const foundRow = rows.find(r => r[`Allowance${col.idx}Name`]);
                  label = foundRow ? foundRow[`Allowance${col.idx}Name`] : `Allowance ${col.idx}`;
                } else if (col.isDeduction) {
                  const foundRow = rows.find(r => r[`Deduction${col.idx}Name`]);
                  label = foundRow ? foundRow[`Deduction${col.idx}Name`] : `Deduction ${col.idx}`;
                }
                return (
                  <th key={ci} style={thCol(col.w, ci >= selRange.c1 && ci <= selRange.c2)}>
                    <span style={{ marginRight: "3px", opacity: 0.6, fontWeight: 400, fontSize: "10px" }}>{colLetter(ci)}</span>
                    {label}
                    {col.readOnly && <span style={{ marginLeft: "3px", opacity: 0.6, fontSize: "9px" }}>⚙</span>}
                  </th>
                );
              })}
              <th style={{ ...TH_NUM, width: "36px", minWidth: "36px" }}></th>
            </tr>
          </thead>
          <tbody>
            {rows.slice(startIndex, endIndex).map((row, localRi) => {
              const ri = startIndex + localRi;
              return (
                <GridRow
                  key={ri}
                  row={row}
                  ri={ri}
                  visCOLS={visCOLS}
                  isFocused={focus.r === ri}
                  focusedCol={focus.r === ri ? focus.c : -1}
                  editMode={editMode}
                  isSelected={ri >= selRange.r1 && ri <= selRange.r2}
                  selStartCol={selRange.c1}
                  selEndCol={selRange.c2}
                  isHovered={hoverRowIdx === ri}
                  isColumnReadOnly={isColumnReadOnly}
                  handleCellClick={handleCellClick}
                  enterEditMode={enterEditMode}
                  setCell={setCell}
                  delRow={delRow}
                  getAllowanceBreakdown={getAllowanceBreakdown}
                  getDeductionBreakdown={getDeductionBreakdown}
                  employees={employees}
                  inputRef={inputRef}
                  handleKey={handleKey}
                  doPaste={doPaste}
                  gotoCell={gotoCell}
                  editValue={editValue}
                  setLocalEditValue={setLocalEditValue}
                  editValueRef={editValueRef}
                  editModeRef={editModeRef}
                  focusedCellRef={focusedCellRef}
                  setHoverRowIdx={setHoverRowIdx}
                  setEditMode={setEditMode}
                  displayVal={displayVal}
                />
              );
            })}
            {/* Add row footer */}
            <tr>
              <td colSpan={visCOLS.length + 2}
                onClick={addNewRow}
                style={{ border: "1px dashed #b0b0b0", textAlign: "center", color: "#aaa", fontSize: "12px", cursor: "pointer", height: "28px", background: "#fafafa", userSelect: "none" }}>
                ＋ Click to add a new row
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      {/* ─── Pagination Toolbar ─── */}
      <div style={{
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        padding: "6px 16px",
        background: "#e8f5e9",
        borderTop: "1px solid #ced4da",
        borderBottom: "1px solid #ced4da",
        fontSize: "12px",
        flexShrink: 0,
        userSelect: "none"
      }}>
        <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
          <span style={{ fontWeight: 600, color: "#217346" }}>Show:</span>
          <select
            value={pageSize}
            onChange={e => handlePageSizeChange(parseInt(e.target.value))}
            style={{ padding: "2px 6px", borderRadius: "4px", border: "1px solid #ced4da", fontSize: "12px", outline: "none", background: "#fff", cursor: "pointer" }}
          >
            <option value={10}>10 rows</option>
            <option value={20}>20 rows</option>
            <option value={50}>50 rows</option>
            <option value={100}>100 rows</option>
          </select>
          <span style={{ color: "#555" }}>
            Showing <strong>{rows.length > 0 ? startIndex + 1 : 0}</strong> - <strong>{endIndex}</strong> of <strong>{rows.length}</strong> rows
          </span>
        </div>

        <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
          <button
            onClick={() => handlePageChange(1)}
            disabled={currentPage === 1}
            className="btn btn-xs btn-outline-success"
            style={{ padding: "2px 8px", fontSize: "11px", cursor: currentPage === 1 ? "default" : "pointer" }}
          >
            « First
          </button>
          <button
            onClick={() => handlePageChange(currentPage - 1)}
            disabled={currentPage === 1}
            className="btn btn-xs btn-outline-success"
            style={{ padding: "2px 8px", fontSize: "11px", cursor: currentPage === 1 ? "default" : "pointer" }}
          >
            ‹ Prev
          </button>
          <span style={{ padding: "0 8px", fontWeight: 700, color: "#217346" }}>
            Page {currentPage} of {totalPages}
          </span>
          <button
            onClick={() => handlePageChange(currentPage + 1)}
            disabled={currentPage === totalPages}
            className="btn btn-xs btn-outline-success"
            style={{ padding: "2px 8px", fontSize: "11px", cursor: currentPage === totalPages ? "default" : "pointer" }}
          >
            Next ›
          </button>
          <button
            onClick={() => handlePageChange(totalPages)}
            disabled={currentPage === totalPages}
            className="btn btn-xs btn-outline-success"
            style={{ padding: "2px 8px", fontSize: "11px", cursor: currentPage === totalPages ? "default" : "pointer" }}
          >
            Last »
          </button>
        </div>
      </div>

      {/* ─── Status bar ─── */}
      <div style={{ flexShrink: 0, padding: "3px 12px", background: "#e8f5e9", borderTop: "1px solid #a5d6a7", fontSize: "12px", color: "#555", display: "flex", gap: "24px", alignItems: "center" }}>
        <span>Cell: <strong style={{ color: "#217346" }}>{colLetter(focus.c)}{focus.r + 1}</strong></span>
        {isMultiSel && (
          <span>Selection: <strong style={{ color: "#217346" }}>{colLetter(selRange.c1)}{selRange.r1 + 1}:{colLetter(selRange.c2)}{selRange.r2 + 1}</strong></span>
        )}
        <span style={{ marginLeft: "auto" }}>
          Mode: <strong style={{ color: "#217346" }}>{editMode ? "EDIT" : "READY"}</strong>
        </span>
      </div>
    </div>
  );
};

export default MultiPayrollEntry;
